import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import DateWarnings, { useDateWarnings } from '../components/DateWarnings';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { tasksAPI, projectsAPI, usersAPI, exportAPI, downloadBlob } from '../utils/api';
import { PageHeader, Badge, SearchInput, Modal, FormField, SelectField, EmptyState, ConfirmDialog } from '../components/layout/UI';
import { format, parseISO, isValid } from 'date-fns';
import { useToast, apiError } from '../components/Toast';
import { useMaster } from '../hooks/useMasters';
import { usePagePerms } from '../PagePermsContext';
import { Calendar, CheckSquare, Download, Layers, Plus, RotateCcw, SquarePen, Trash2, X } from 'lucide-react';
import { getPermLevel, isAdminRole } from '../utils/rolesConfig';
import Pager, { usePaging } from '../components/Pager';
import { useSelection, SelectAll, RowCheck, BulkBar } from '../components/BulkActions';
import { STATUS_OPTIONS } from '../utils/workStatus';

// ── Icons ──────────────────────────────────────────────────────────────────
const PlusIcon = () => <Plus size={14} />;
const PencilIcon = () => <SquarePen size={13} />;
const TrashIcon = () => <Trash2 size={13} />;
const LayersIcon = () => <Layers size={13} />;
const CalIcon = () => <Calendar size={11} />;
const DlIcon = () => <Download size={14} />;

// ── Helpers ────────────────────────────────────────────────────────────────
const fmt = (val, pattern = 'MMM d, yyyy') => {
  if (!val) return '—';
  try { const d = typeof val === 'string' ? parseISO(val) : new Date(val); return isValid(d) ? format(d, pattern) : '—'; }
  catch { return '—'; }
};

const EffortBar = ({ estimated, actual, over, source }) => {
  const est = parseFloat(estimated) || 0, act = parseFloat(actual) || 0;
  if (!est) return <span className="text-xs" style={{ color:'var(--text-secondary)' }}>—</span>;
  const pct = Math.min(100, Math.round((act/est)*100));
  const isOver = act > est;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background:'var(--border)', minWidth:50 }}>
        <div className="h-full rounded-full" style={{ width:`${pct}%`, background:isOver?'#ef4444':'#2E539F' }} />
      </div>
      <span className="text-xs whitespace-nowrap" style={{ color:isOver?'#ef4444':'var(--text-secondary)' }}>
        {act.toFixed(1)}/{est.toFixed(1)}h
      </span>
      {isOver && (
        <span className="text-[11px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap"
          style={{ background:'rgba(239,68,68,.12)', color:'#ef4444' }}
          title={`${over ? `${over} ${over===1?'activity':'activities'} over estimate` : 'Over estimate'}${source==='activities' ? ' · rolled up from activities' : ''}`}>
          +{(act-est).toFixed(1)}h
        </span>
      )}
    </div>
  );
};

const STATUS_OPTS_FALLBACK   = STATUS_OPTIONS;
const PRIORITY_OPTS_FALLBACK = ['Low','Medium','High','Critical'].map(v=>({value:v,label:v}));
const WORK_OPTS_FALLBACK     = ['Implementation','Training','Bug Fix','Change Request','Support'].map(v=>({value:v,label:v}));
const defaultForm   = { project_id:'', title:'', description:'', priority:'Medium', status:'Pending', work_type:'Implementation', client:'', module:'', start_date:'', due_date:'', assigned_to:'', estimated_hours:'' };

// ── Main page ──────────────────────────────────────────────────────────────
export default function TasksPage() {
  // Admin → Page Access decides whether this role may create here.
  const { canWrite } = usePagePerms();
  // Dropdowns come from Masters so an Admin can relabel and reorder them.
  // The _FALLBACK arrays above are used when Masters has nothing configured.
  const STATUS_OPTS = useMaster('task_status', STATUS_OPTS_FALLBACK);
  const PRIORITY_OPTS = useMaster('priority', PRIORITY_OPTS_FALLBACK);
  const WORK_OPTS = useMaster('work_type', WORK_OPTS_FALLBACK);
  const toast = useToast();
  const { user: me } = useAuth();
  /** Mirrors the API: creator, assignee, or admin. */
  const canRemove = (t) =>
    isAdminRole(me?.role) || getPermLevel(me?.role) === 'Manager' || t.created_by === me?.id || t.assigned_to === me?.id;
  const dateWarn = useDateWarnings();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('project_id') || '';

  const navigate = useNavigate();
  const [tasks,         setTasks]         = useState([]);
  const [error, setError] = useState('');
  const [projects,      setProjects]      = useState([]);
  const [users,         setUsers]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState('');
  /**
   * One control, three answers: everything, mine, and what I deleted.
   *
   * This replaces the All/Mine toggle rather than sitting beside it — the
   * three are mutually exclusive, and two controls that can disagree is how
   * you end up looking at an empty list and not knowing why.
   */
  const [scope, setScope] = useState('all');   // all | mine | deleted
  // One pager for every list, so they all count and phrase it the same way.
  const paging = usePaging(scope);

  /**
   * The admin dashboard links here with ?overdue=1 or ?unassigned=1. Honouring
   * them is the difference between "9 Overdue tasks" showing you nine and
   * showing you everything, which looks like it worked.
   */
  const [sp, setSp] = useSearchParams();
  const overdue    = sp.get('overdue')    === '1';
  const unassigned = sp.get('unassigned') === '1';
  const clearFocus = () => { const n = new URLSearchParams(sp); n.delete('overdue'); n.delete('unassigned'); setSp(n); };
  const [typeFilter,    setTypeFilter]    = useState('');
  const [modalOpen,     setModalOpen]     = useState(false);
  const [editTask,      setEditTask]      = useState(null);
  const [form,          setForm]          = useState({ ...defaultForm, project_id: projectFilter });
  const [saving,        setSaving]        = useState(false);
  const [deleteId,      setDeleteId]      = useState(null);





  const discard = async (t) => {
    const ok = await confirm(
      'Remove this from your Deleted tab? You will not be able to see it after this, '
      + 'though an administrator can still restore it.',
      { title: 'Remove from Deleted', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await tasksAPI.discard(t.id);
      toast.success('Removed from your Deleted tab');
      load();
    } catch (e) { toast.error(apiError(e, 'Could not remove it.')); }
  };
  const restore = async (t) => {
    try {
      await tasksAPI.restore(t.id);
      toast.success(`"${t.title}" restored`);
      load();
    } catch (e) { toast.error(apiError(e, 'Could not restore that task.')); }
  };
  const load = useCallback(async () => {
    try {
      const [tR, pR, uR] = await Promise.all([
        tasksAPI.getAll({ project_id:projectFilter, search, status:statusFilter, work_type:typeFilter,
                          mine: scope === 'mine' ? 'true' : undefined,
                          scope: scope === 'deleted' ? 'deleted' : undefined,
                          overdue: overdue ? '1' : undefined,
                          unassigned: unassigned ? '1' : undefined, limit: paging.limit, offset: paging.offset }),
        projectsAPI.getAll({ limit: 0 }),
        usersAPI.getAll(),
      ]);
      setTasks(tR.data); setProjects(pR.data); setUsers(uR.data);
      paging.readHeaders(tR);
    } catch(e) { toast.error(apiError(e, 'Failed to save task. Check your permissions.')); }
    finally { setLoading(false); }
  }, [projectFilter, search, statusFilter, typeFilter, scope, overdue, unassigned, paging.offset]);

  useEffect(() => { load(); }, [load]);

  /** Browsers grey out-of-range days inconsistently, and a value can still be
   *  typed or pasted. Clamping on change makes the bound real rather than a
   *  suggestion: an out-of-range date snaps to the nearest allowed one. */
  const clamp = (value, { min, max }) => {
    if (!value) return value;
    if (min && value < min) return min;
    if (max && value > max) return max;
    return value;
  };

  /** Spelled out under the fields, because a greyed-out calendar day does not
   *  explain itself and browsers grey them inconsistently. */
  const rangeHint = (b, who) =>
    b.min || b.max
      ? `Must fall within the ${who}: ${b.min || 'any'} to ${b.max || 'any'}`
      : `This ${who} has no dates set, so there is nothing to bound to`;

  /** The parent's window, as min/max for the date pickers. The browser then
   *  greys out everything outside it, so an out-of-range date cannot be
   *  chosen by mistake rather than being warned about after the fact. */
  const bounds = (projectId) => {
    const p = projects.find(x => x.id === projectId);
    return { min: p?.start_date?.split('T')[0] || '', max: p?.end_date?.split('T')[0] || '' };
  };

  /** Parent dates as a starting point — the user can still change them. */
  const inheritDates = (projectId) => {
    const p = projects.find(x => x.id === projectId);
    return p ? { start_date: p.start_date?.split('T')[0] || '',
                 due_date:   p.end_date?.split('T')[0]   || '' }
             : { start_date:'', due_date:'' };
  };

  const openCreate = () => {
    setEditTask(null);
    setForm({ ...defaultForm, project_id:projectFilter, assigned_to: me?.id || '',
              ...inheritDates(projectFilter) });
    setModalOpen(true);
  };
  const openEdit   = (t) => {
    setEditTask(t);
    setForm({ project_id:t.project_id, title:t.title, description:t.description||'', priority:t.priority, status:t.status, work_type:t.work_type, client:t.client||'', module:t.module||'', start_date:t.start_date?.split('T')[0]||'', due_date:t.due_date?.split('T')[0]||'', assigned_to:t.assigned_to||'',
      estimated_hours: t.estimated_hours ?? '' });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = editTask ? await tasksAPI.update(editTask.id, form)
                             : await tasksAPI.create(form);
      toast.success(editTask ? 'Task updated' : 'Task created');
      // The API allows a task outside its project's window but says so.
      dateWarn.show(saved.data);
      setModalOpen(false); load();
    } catch(e) { toast.error(apiError(e, 'Failed to save task')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await tasksAPI.delete(deleteId);
    toast.success('Task deleted'); setDeleteId(null); load(); } catch(e) { toast.error(apiError(e, 'Failed to delete task')); }
  };

  // Ticked rows. Scoped to what is on screen, so a filter cannot hide
  // something that is still selected.
  const sel = useSelection(tasks);

  const handleExport = async () => {
    try { const r = await exportAPI.exportTasks(projectFilter); downloadBlob(r.data,'tasks.xlsx'); } catch(e) { toast.warning('Export failed'); }
  };

  return (
    <div>
      <DateWarnings {...dateWarn} />
      <PageHeader title="Tasks" subtitle="Track all implementation and training tasks"
        actions={
          <>
            <button onClick={handleExport} className="btn-secondary flex items-center gap-1.5 text-sm"><DlIcon /> Export</button>
            {canWrite && (<button onClick={openCreate}   className="btn-primary  flex items-center gap-1.5 text-sm"><PlusIcon /> New Task</button>)}
          </>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex-1 min-w-48 max-w-xs">
          <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..." />
        </div>
        <select className="select w-36 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="select w-44 text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {WORK_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {(overdue || unassigned) && (
          <button onClick={clearFocus}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm self-start"
            style={{ background:'rgba(255,200,73,.20)', color:'#8A6400' }}>
            {overdue ? 'Overdue only' : 'Unassigned only'}
            <X size={13} />
          </button>
        )}
        {/* All / Mine / Deleted. "Mine" is assigned to me OR created by me.
            "Deleted" shows only what you could have seen while it was live —
            deletion must never reveal something that was hidden from you. */}
        <div className="flex rounded-lg border overflow-hidden text-sm self-start"
          style={{ borderColor:'var(--border)' }}>
          {[['all', 'All'], ['mine', 'Mine'], ['deleted', 'Deleted']].map(([v, l]) => (
            <button key={v} onClick={() => setScope(v)}
              className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${scope===v ? 'bg-brand-500 text-white' : ''}`}
              style={scope!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
              {v === 'deleted' && <Trash2 size={13} />}
              {l}
              {scope===v && <span className="font-bold">{tasks.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : tasks.length === 0 ? (
        <div className="card"><EmptyState icon={CheckSquare} title="No tasks found" description="Create your first task"
          action={canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIcon /> New Task</button>)} /></div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                <th className="px-4 py-3 w-10">
                  <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                    onToggle={sel.toggleAll} />
                </th>
                {['Title','Project','Type','Status','Priority','Due','Effort','Assigned',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={{ color:'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.id}
                  className={`border-b hover:bg-[var(--surface-alt)] transition-colors cursor-pointer`}
                  style={{
                    borderColor: 'var(--border)',
                    borderLeft: t.is_delegated ? '3px solid #5CC3BC' : undefined,
                    background: t.is_delegated ? 'rgba(92,195,188,0.04)' : undefined,
                  }}
                  onClick={() => navigate(`/tasks/${t.id}`)}>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <RowCheck checked={sel.isSelected(t.id)}
                      onToggle={() => sel.toggle(t.id)} label={t.title} />
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium" style={{ color:'var(--text)' }}>{t.title}</p>
                      {t.is_delegated && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                          style={{ background:'rgba(92,195,188,0.15)', color:'#0f766e', border:'1px solid rgba(92,195,188,0.4)' }}
                          title={`Delegated by ${t.assigned_to_name} — you are covering this task`}>
                          🛡 Covering for {t.assigned_to_name?.split(' ')[0]}
                        </span>
                      )}
                    </div>
                    {t.module && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{t.module}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs hidden md:table-cell" style={{ color:'var(--text-secondary)' }}>{t.project_name}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="text-xs px-2 py-0.5 rounded" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>{t.work_type}</span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {/* A deleted task's status is a fact about when it was
                        deleted, not something to change. Restore it first. */}
                    <select value={t.status} disabled={scope === 'deleted'}
                      onChange={async e => {
                        try {
                          await tasksAPI.update(t.id, { status:e.target.value });
                          toast.success(`Status set to ${e.target.value}`);
                        } catch(err) { toast.error(apiError(err, 'Could not change that status.')); }
                        finally { load(); }
                      }}
                      className="text-xs rounded-lg px-2 py-1 border font-medium cursor-pointer outline-none"
                      style={{ background:'var(--surface)', borderColor:'var(--border)', color:'var(--text)' }}>
                      {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell"><Badge value={t.priority} type="priority" /></td>
                  <td className="px-4 py-3 text-xs hidden xl:table-cell" style={{ color:'var(--text-secondary)' }}>
                    <span className="flex items-center gap-1"><CalIcon />{fmt(t.due_date,'MMM d')}</span>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell" style={{ minWidth:110 }}>
                    <EffortBar estimated={t.effort_estimated ?? t.estimated_hours}
                      actual={t.effort_actual ?? t.actual_hours}
                      over={Number(t.act_over_count) || 0}
                      source={t.effort_source} />
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {t.assigned_to_name
                      ? <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded text-xs font-bold text-white flex items-center justify-center"
                            style={{ background: t.assigned_to_color || '#234180' }}>{t.assigned_to_name[0]}</div>
                          <span className="text-xs truncate max-w-[80px]" style={{ color:'var(--text-secondary)' }}>{t.assigned_to_name}</span>
                        </div>
                      : <span className="text-xs" style={{ color:'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      {scope !== 'deleted' && (
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${t.id}`); }}
                          className="p-1.5 rounded hover:bg-brand-50 dark:hover:bg-brand-900/20 text-brand-500 transition-colors"
                          title="View activities"><LayersIcon /></button>
                      )}
                      {scope === 'deleted' ? (
                        <>
                        {/* In the bin, the only sensible action is to put it
                           back. Editing a deleted task, or deleting it twice,
                           are both nonsense. */}
                        <button onClick={(e) => { e.stopPropagation(); restore(t); }}
                          className="px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 hover:bg-[var(--surface-alt)]"
                          style={{ color:'#2E539F' }} title="Restore this task">
                          <RotateCcw size={13} /> Restore
                        </button>
                        {/* The second delete. It removes this from YOUR Deleted tab
                            and nothing more — an administrator can still get it
                            back. Nobody but an administrator destroys anything. */}
                        <button onClick={(e) => { e.stopPropagation(); discard(t); }}
                          className="px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 hover:bg-red-50 dark:hover:bg-red-900/20"
                          style={{ color:'#ef4444' }} title="Remove from your Deleted tab">
                          <Trash2 size={13} /> Delete
                        </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-[var(--surface-alt)]"
                            style={{ color:'var(--text-secondary)' }}><PencilIcon /></button>
                          {canRemove(t) && (
                            <button onClick={() => setDeleteId(t.id)} className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><TrashIcon /></button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editTask ? 'Edit Task' : 'New Task'} size="lg">
        <div className="space-y-4">
          <FormField label="Project" required>
            <select className="select" value={form.project_id}
              onChange={e => { const pid = e.target.value; const d = inheritDates(pid);
                setForm(f => ({ ...f, project_id: pid,
                  start_date: f.start_date || d.start_date,
                  due_date:   f.due_date   || d.due_date })); }}>
              <option value="">Select project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormField>
          <FormField label="Title" required>
            <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title:e.target.value }))} placeholder="Task title" />
          </FormField>
          <FormField label="Description">
            <textarea className="input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description:e.target.value }))} placeholder="Describe the task..." />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Work Type">
              <SelectField value={form.work_type} onChange={e => setForm(f => ({ ...f, work_type:e.target.value }))} options={WORK_OPTS} placeholder="" />
            </FormField>
            <FormField label="Priority">
              <SelectField value={form.priority} onChange={e => setForm(f => ({ ...f, priority:e.target.value }))} options={PRIORITY_OPTS} placeholder="" />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status">
              <SelectField value={form.status} onChange={e => setForm(f => ({ ...f, status:e.target.value }))} options={STATUS_OPTS} placeholder="" />
            </FormField>
            <FormField label="Assigned To">
              <select className="select" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to:e.target.value }))}>
                <option value="">Unassigned</option>
                {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Start Date">
              <input type="date" className="input" value={form.start_date}
                min={bounds(form.project_id).min || undefined}
                max={(form.due_date || bounds(form.project_id).max) || undefined}
                title={rangeHint(bounds(form.project_id), 'project')}
                onChange={e => setForm(f => ({ ...f, start_date: clamp(e.target.value,
                  { min: bounds(f.project_id).min, max: f.due_date || bounds(f.project_id).max }) }))} />
            </FormField>
            <FormField label="Due Date">
              <input type="date" className="input" value={form.due_date}
                min={(form.start_date || bounds(form.project_id).min) || undefined}
                max={bounds(form.project_id).max || undefined}
                title={rangeHint(bounds(form.project_id), 'project')}
                onChange={e => setForm(f => ({ ...f, due_date: clamp(e.target.value,
                  { min: f.start_date || bounds(f.project_id).min, max: bounds(f.project_id).max }) }))} />
            </FormField>
            <div className="col-span-2 -mt-2">
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                {rangeHint(bounds(form.project_id), 'project')}
              </p>
            </div>
            <FormField label="Est. Hours">
              <input type="number" min="0" step="0.5" className="input" placeholder="e.g. 8"
                value={form.estimated_hours} onChange={e => setForm(f => ({ ...f, estimated_hours:e.target.value }))} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Client">
              <input className="input" value={form.client} onChange={e => setForm(f => ({ ...f, client:e.target.value }))} placeholder="Client" />
            </FormField>
            <FormField label="Module">
              <input className="input" value={form.module} onChange={e => setForm(f => ({ ...f, module:e.target.value }))} placeholder="Module" />
            </FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.project_id || !form.title || !form.assigned_to} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin" />}
              {editTask ? 'Update' : 'Create'} Task
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Delete Task" message="Delete this task? You can restore it from the Deleted tab." />

    <Pager {...paging} onChange={paging.setOffset} noun="tasks" />
    <BulkBar bulk={tasksAPI.bulk} view={scope === 'deleted' ? 'deleted' : 'live'}
      selected={sel.selected} label="task" labelPlural="tasks"
      onClear={sel.clear} onDone={load} />
    </div>
  );
}
